import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';
import './RegisterWithBaby.css';
import { API_BASE_URL } from '../config';

interface RegisterWithBabyProps {
  onRegistrationSuccess?: (user: any) => void;
}

type Step = 'baby' | 'email' | 'time-format' | 'nap-count' | 'sleep-times' | 'verify';

// Helper function to calculate baby's age in weeks from birth date
const calculateAgeWeeks = (birthDateString: string): number | null => {
  if (!birthDateString) return null;

  const birthDate = new Date(birthDateString);
  const today = new Date();
  const ageDays = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.floor(ageDays / 7);
};

// Helper function to get typical nap range for a given age in months
const getTypicalNapRange = (ageMonths: number): { min: number; max: number } | null => {
  if (ageMonths < 0) return null;

  // Use 12-month range for babies older than 12 months
  const lookupMonth = ageMonths > 12 ? 12 : ageMonths;

  const napRanges: { [key: number]: { min: number; max: number } } = {
    0: { min: 4, max: 6 },   // 0-2 months
    1: { min: 4, max: 6 },
    2: { min: 4, max: 6 },
    3: { min: 3, max: 4 },   // 3-5 months
    4: { min: 3, max: 4 },
    5: { min: 3, max: 4 },
    6: { min: 2, max: 3 },   // 6-8 months
    7: { min: 2, max: 3 },
    8: { min: 2, max: 3 },
    9: { min: 2, max: 2 },   // 9-12 months
    10: { min: 2, max: 2 },
    11: { min: 2, max: 2 },
    12: { min: 1, max: 2 },  // 12+ months
  };

  return napRanges[lookupMonth] || { min: 1, max: 2 };
};

const RegisterWithBaby: React.FC<RegisterWithBabyProps> = ({ onRegistrationSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('baby');

  // Form data
  const [babyName, setBabyName] = useState('');
  const [babyBornDate, setBabyBornDate] = useState('');
  const [email, setEmail] = useState('');
  const [timeFormat24h, setTimeFormat24h] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Sleep preferences
  const [targetNapCount, setTargetNapCount] = useState<number | null>(null);
  const [preferredBedtime, setPreferredBedtime] = useState('');
  const [preferredWakeTime, setPreferredWakeTime] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const getTotalSteps = () => 4; // Reduced from 6 (removed nap-count and sleep-times steps)

  const getCurrentStepNumber = () => {
    switch (currentStep) {
      case 'baby': return 1;
      case 'nap-count': return 2; // Hidden but kept for easy re-enable
      case 'sleep-times': return 3; // Hidden but kept for easy re-enable
      case 'time-format': return 2; // Now step 2
      case 'email': return 3; // Now step 3
      case 'verify': return 4; // Now step 4
    }
  };

  const handleBabySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Skip sleep preferences for now - go straight to time format and email
    // To re-enable: change to setCurrentStep('nap-count')
    setCurrentStep('time-format');
  };

  const handleNapCountSubmit = () => {
    setError('');
    setCurrentStep('sleep-times');
  };

  const handleSleepTimesSubmit = () => {
    setError('');
    setCurrentStep('time-format');
  };

  const handleTimeFormatSubmit = () => {
    setError('');
    setCurrentStep('email');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register-with-baby`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          baby_name: babyName,
          baby_born_date: babyBornDate,
          time_format_24h: timeFormat24h,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      // Move to verification step
      setCurrentStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Verification failed');
      }

      const user = await response.json();

      // Save sleep preferences if any were set
      const hasPreferences = targetNapCount !== null || preferredBedtime || preferredWakeTime;
      if (hasPreferences && user.active_baby_id) {
        try {
          await fetch(`${API_BASE_URL}/api/sleep-preferences/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              baby_id: user.active_baby_id,
              target_nap_count: targetNapCount,
              preferred_bedtime: preferredBedtime || null,
              preferred_wake_time: preferredWakeTime || null,
            }),
          });
          // Don't fail registration if preferences save fails
        } catch (prefError) {
          console.error('Failed to save sleep preferences:', prefError);
        }
      }

      if (onRegistrationSuccess) {
        onRegistrationSuccess(user);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to resend code');
      }

      alert('Verification code resent to your email!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    switch (currentStep) {
      case 'nap-count':
        setCurrentStep('baby');
        break;
      case 'sleep-times':
        setCurrentStep('nap-count');
        break;
      case 'time-format':
        setCurrentStep('sleep-times');
        break;
      case 'email':
        setCurrentStep('time-format');
        break;
      case 'verify':
        setCurrentStep('email');
        break;
      case 'baby':
        navigate('/');
        break;
    }
  };

  // Step 1: Baby Information
  if (currentStep === 'baby') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Tell us about your baby</h2>
          <p>Let's start tracking sleep together</p>

          <form onSubmit={handleBabySubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="babyName">Baby's Name</label>
              <input
                type="text"
                id="babyName"
                value={babyName}
                onChange={(e) => {
                  setBabyName(e.target.value);
                  setError('');
                }}
                required
                placeholder="Baby's name"
                autoComplete="off"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="babyBornDate">Birth Date</label>
              <input
                type="date"
                id="babyBornDate"
                value={babyBornDate}
                onChange={(e) => {
                  setBabyBornDate(e.target.value);
                  setError('');
                }}
                required
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={!babyName || !babyBornDate}
              className="btn-primary"
            >
              Next
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 2: Target Naps
  if (currentStep === 'nap-count') {
    // Calculate baby age and typical nap range for guidance
    const babyAgeWeeks = calculateAgeWeeks(babyBornDate);
    const ageMonths = babyAgeWeeks !== null ? Math.floor(babyAgeWeeks / 4) : null;
    const napRange = ageMonths !== null ? getTypicalNapRange(ageMonths) : null;

    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Target Naps Per Day</h2>
          <p>How many naps would you like your baby to have? (Optional)</p>
          <p style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.6)', marginTop: '-0.5rem', marginBottom: '1.5rem' }}>
            Setting target naps per day is optional, but setting it will help us suggest when your little one may sleep
          </p>

          <div className="register-form">
            <div className="form-group">
              <label htmlFor="nap-count">Target naps per day</label>
              <select
                id="nap-count"
                value={targetNapCount === null ? '' : targetNapCount}
                onChange={(e) => setTargetNapCount(e.target.value === '' ? null : parseInt(e.target.value))}
                disabled={isLoading}
              >
                <option value="">No preference</option>
                <option value="0">0 naps</option>
                <option value="1">1 nap</option>
                <option value="2">2 naps</option>
                <option value="3">3 naps</option>
                <option value="4">4 naps</option>
                <option value="5">5 naps</option>
              </select>
              {babyAgeWeeks !== null && napRange !== null && (
                <p style={{ marginTop: '8px', fontSize: '0.9em', fontStyle: 'italic', color: '#666' }}>
                  A baby at {babyAgeWeeks} weeks normally has between {napRange.min} to {napRange.max} naps
                </p>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              onClick={handleNapCountSubmit}
              disabled={isLoading}
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Sleep Times
  if (currentStep === 'sleep-times') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Preferred Sleep Times</h2>
          <p>When would you like your baby to sleep and wake? (Optional)</p>

          <div className="register-form">
            <div className="form-group">
              <label htmlFor="bedtime">Preferred bedtime</label>
              <input
                type="time"
                id="bedtime"
                value={preferredBedtime}
                onChange={(e) => setPreferredBedtime(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="wake-time">Preferred wake time</label>
              <input
                type="time"
                id="wake-time"
                value={preferredWakeTime}
                onChange={(e) => setPreferredWakeTime(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              onClick={handleSleepTimesSubmit}
              disabled={isLoading}
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Time Format
  if (currentStep === 'time-format') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Preferred Time Format</h2>
          <p>How would you like to see times displayed?</p>

          <div className="register-form">
            <div className="form-group">
              <div className="time-format-options">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="timeFormat"
                    checked={!timeFormat24h}
                    onChange={() => setTimeFormat24h(false)}
                  />
                  <span>12-hour (e.g., 2:30 PM)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="timeFormat"
                    checked={timeFormat24h}
                    onChange={() => setTimeFormat24h(true)}
                  />
                  <span>24-hour (e.g., 14:30)</span>
                </label>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              onClick={handleTimeFormatSubmit}
              disabled={isLoading}
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 5: Email
  if (currentStep === 'email') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>How can we reach you?</h2>
          <p>We'll send you a code to verify your email</p>

          <form onSubmit={handleEmailSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="email">Your Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                required
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={!email || isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 6: Email Verification
  if (currentStep === 'verify') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Check Your Email</h2>
          <p>We've sent a verification code to {email}</p>

          <form onSubmit={handleVerify} className="register-form">
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                  setError('');
                }}
                required
                placeholder="Enter 10-digit code"
                maxLength={10}
                autoComplete="off"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={isLoading || verificationCode.length !== 10}
              className="btn-primary"
            >
              {isLoading ? 'Verifying...' : 'Verify & Start'}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="btn-secondary"
            >
              Resend Code
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default RegisterWithBaby;
